import { FeatureToggles } from '@/features/feature-toggles'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type Member } from '@/features/iam/members/Member'
import { Team } from '@/features/iam/teams/Team'
import { Toggle } from '@/infra/Toggle'
import { orderBy } from 'lodash-es'
import { TextMessageContentViewModel } from './TextMessageContentViewModel'
import {
  type ConversationStore,
  type SendMessageParams,
} from './ConversationStore'
import { Conversation } from './Conversation'
import { type Message } from './Message'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type { CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { downloadPdf, type PDFElements } from './pdf-util'
import { JobProgressionMessageContentViewModel } from './JobProgressionMessageContentViewModel'
import {
  MessageViewModel,
  type MessageContentViewModel,
} from './MessageViewModel'
import { Forbidden } from 'fejl'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { JobProgressionFn } from '@/features/jobs'
import { isSameDay } from 'date-fns'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import { catchError, EMPTY, filter, switchMap } from 'rxjs'

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: '2-digit',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

/**
 * Conversation view model.
 */
export class ConversationViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The conversation whose first page of messages has been fetched.
   */
  private _fetchedConversation: Conversation | null = null

  /**
   * View models for the loaded messages.
   */
  private readonly _messageVms = new ViewModelList({
    models: () => this.conversation.messages.slice(),
    create: (message: Message) =>
      new MessageViewModel(
        message,
        this._createMessageContentViewModel(message),
        this._conversationStore,
        () => this.canEditMessage,
        () => this.editingMessageId,
        this.setEditingMessageId,
        this.updateMessage,
        () => this.updateMessage.pending,
      ),
    activate: (vm) => vm.activate(),
    deactivate: (vm) => vm.deactivate(),
  })

  /**
   * Whether the legacy chat composer is in note mode.
   */
  readonly noteMode = new Toggle(false)

  /**
   * The text being written by the user.
   */
  @observable accessor composerText = ''

  /**
   * The message id in edit mode.
   */
  @observable accessor editingMessageId = Id.none

  /**
   * Conversation scroller.
   */
  readonly scroller = new InfiniteScrollProvider({
    limit: 20,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Unable to load messages',
        signal,
      )

      return withRetry(() =>
        Forbidden.retry(() => this._listMessages(paging, signal), {
          tries: 10,
          minTimeout: 400,
          maxTimeout: 1_000,
        }),
      )
    },
  })

  constructor(
    private readonly _getConversation: () => Conversation,
    private readonly _team: Team = Team.none,
    private readonly _options: ConversationViewModelOptions,
    private readonly _conversationStore: ConversationStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _jobProgression: JobProgressionFn,
  ) {
    this._lifecycle.manage(this._messageVms).manage(
      toStream(() => this.conversation, true).pipe(
        filter(
          (conversation) =>
            conversation !== Conversation.none &&
            conversation !== this._fetchedConversation,
        ),
        switchMap((conversation) =>
          withAbortSignal((signal) =>
            this._fetchInitial(conversation, signal),
          ).pipe(
            // The view renders a failed fetch with a retry from the
            // scroller's state.
            catchError(() => EMPTY),
          ),
        ),
      ),
    )
  }

  /**
   * The conversation being shown.
   */
  @computed
  get conversation() {
    return this._getConversation()
  }

  /**
   * Whether an admin outside the conversation can refetch the latest messages.
   */
  @computed
  get canRefreshManually() {
    const currentMember = this._getCurrentMember(this.conversation.workspace.id)
    const isAdmin = currentMember.permissions.adminJobView
    const isPartOfConvo = this.conversation.participants.some(
      (p) => p.member.user.publicId === currentMember.user.publicId,
    )

    return isAdmin && !isPartOfConvo
  }

  /**
   * Whether the current user muted the conversation.
   */
  @computed
  get muted() {
    const currentUserPublicId = Id.assert(
      this._getCurrentMember().user.publicId,
    )
    const participant = this.conversation.participants.find(
      (p) => Id.assert(p.member.user.publicId) === currentUserPublicId,
    )
    return participant?.muted ?? false
  }

  /**
   * Whether the workspace offers the message bank in the composer.
   */
  @computed
  get shouldShowMessageBank() {
    const isEnabledAtWorkspace =
      this.conversation.workspace.config.messageBank.enabled

    return isEnabledAtWorkspace
  }

  /**
   * Whether the composer offers note mode. Job notes replace it.
   */
  @computed
  get canToggleNoteMode() {
    return !FeatureToggles.jobNotes()
  }

  /**
   * Whether the composer sends notes instead of messages.
   */
  @computed
  get isNoteModeEnabled() {
    return this.canToggleNoteMode && this.noteMode.isEnabled
  }

  /**
   * Gets the unread count.
   */
  @computed
  get unreadCount() {
    return this.conversation?.unreadCount ?? 0
  }

  /**
   * Conversation messages as view models.
   */
  @computed
  get messages() {
    return orderBy(this._messageVms.items, (vm) => vm.message.dateCreated)
  }

  /**
   * Message groups, grouping messages from the same sender together.
   */
  @computed
  get messageGroups() {
    const messages = this.messages.slice()
    if (messages.length === 0) {
      return []
    }

    const groups: MessageGroup[] = []
    let currentGroup: MessageGroup | null = null
    let previousMessage: MessageViewModel | null = null
    for (const message of messages) {
      const midnightPassedSincePreviousMessage = Boolean(
        previousMessage &&
        !isSameDay(
          previousMessage.message.dateCreated,
          message.message.dateCreated,
        ),
      )
      const isNewSender = Boolean(
        message.message.sender !== currentGroup?.sender,
      )
      const isNonTextContent =
        message.message.content.type !== 'Text' ||
        (previousMessage && previousMessage.message.content.type !== 'Text')

      if (
        !currentGroup ||
        isNewSender ||
        isNonTextContent ||
        midnightPassedSincePreviousMessage
      ) {
        currentGroup = {
          sender: message.message.sender,
          messages: [],
          timestamp: message.message.dateCreated,
          tag: 'None',
          showDateLine:
            groups.length === 0 || midnightPassedSincePreviousMessage,
        }
        groups.push(currentGroup)
      }

      const tag = groupTagForMessage(message)
      if (currentGroup.tag === 'None') {
        currentGroup.tag = tag
      }

      currentGroup.messages.push(message)
      previousMessage = message
    }
    return groups
  }

  /**
   * Checks whether the message can be edited.
   */
  @computed
  get canEditMessage() {
    return this._getCurrentMember().user.platformAdmin
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this.setEditingMessageId(Id.none)
    this._lifecycle.deactivate()
  }

  /**
   * Updates the text of a message.
   */
  updateMessage = task.resolved(async (messageId: Id, text: string) => {
    if (!text) {
      return
    }
    await this._conversationStore
      .updateMessage(this.conversation, messageId, text)
      .catch(this._createAlertErrorHandler('Updating the message failed'))
    this.setEditingMessageId(Id.none)
  })

  /**
   * Sets the message id which will be in edit mode.
   */
  @action.bound
  setEditingMessageId(messageId: Id) {
    this.editingMessageId = messageId
  }

  /**
   * Sets the composer text.
   *
   * @param text
   */
  @action.bound
  setComposerText(text: string) {
    this.composerText = text
  }

  /**
   * Sends a message.
   */
  @action.bound
  async sendMessage() {
    const text = this.composerText
    if (!text) {
      return
    }
    this.setComposerText('')
    const params: SendMessageParams = {
      message: text,
      team: this._team,
      type: this.isNoteModeEnabled ? 'Note' : 'Message',
    }
    await (
      this._options.canManageConversation
        ? this._conversationStore.sendAdminMessage(this.conversation, params)
        : this._conversationStore.sendMessage(this.conversation, params)
    )
      .catch(this._createAlertErrorHandler('Sending the message failed'))
      .catch(() => {
        // Don't overwrite what the user started typing while the send was in
        // flight.
        runInAction(() => {
          if (!this.composerText) {
            this.composerText = text
          }
        })
      })
  }

  /**
   * Fetches the next page of older messages.
   */
  @action.bound
  fetchMore() {
    // Paging while a fetch is in flight would abort it, and paging past the
    // end would start over from the newest page.
    if (!this.scroller.active) {
      return
    }
    // The retry alert reports a failed page.
    void this.scroller.more(this._lifecycle.signal).catch(() => undefined)
  }

  /**
   * Fetches the latest page of messages.
   */
  @action.bound
  async fetchLatestMessages() {
    const msg = this._newAlert()
      .saying('Fetching latest messages!', 'Please wait')
      .show()
    try {
      await this.scroller.fetch({ signal: this._lifecycle.signal })
    } catch (err) {
      msg.dismiss()
      return this._createAlertErrorHandler(
        'Fetching the latest messages failed',
      )(err)
    }
    msg
      .withState('success')
      .saying('Success!', `You're now viewing the latest messages!`)
      .autoDismiss(3000)
  }

  /**
   * Downloads the conversation as PDF
   */
  @action.bound
  async downloadConversation() {
    const msg = this._newAlert()
      .saying('Creating PDF.', 'Please hold on.')
      .show()

    try {
      while (!this.scroller.isDone) {
        await this.scroller.more()
      }
    } catch (err) {
      msg.dismiss()
      return this._createAlertErrorHandler(
        'Downloading the conversation failed',
      )(err)
    }

    const pdfElements: PDFElements[] = [
      {
        type: 'title',
        text: `Conversation History`,
      },
      ...this.messages.map(
        (m) =>
          ({
            type: 'text',
            text: `${dateFormatter.format(m.message.dateCreated)} - ${
              m.message.sender.displayName
            }: ${m.message.text}`,
          }) as PDFElements,
      ),
    ]

    try {
      await downloadPdf(
        pdfElements,
        `Conversation-${dateFormatter.format(new Date())}`,
      )
    } catch (err) {
      msg.dismiss()
      return this._createAlertErrorHandler(
        'Downloading the conversation failed',
      )(err)
    }

    msg
      .withState('success')
      .saying(`PDF has been created!`, `Your download will begin shortly.`)
      .autoDismiss()
      .show()
  }

  /**
   * Toggles muting on the conversation.
   */
  @action.bound
  async toggleMute() {
    const participant = await this._conversationStore
      .toggleMute(this.conversation)
      .catch(this._createAlertErrorHandler('Changing notifications failed'))

    if (participant) {
      this._newAlert()
        .withState('success')
        .saying(
          `Success!`,
          `Conversation notifications ` +
            (participant.muted ? ' muted!' : ' unmuted!'),
        )
        .autoDismiss()
        .show()
    }
  }

  private async _fetchInitial(conversation: Conversation, signal: AbortSignal) {
    await this.scroller.fetch({ signal })
    signal.throwIfAborted()
    this._fetchedConversation = conversation
  }

  private async _listMessages(paging: CursorPageQuery, signal: AbortSignal) {
    return await this._conversationStore.listMessages(
      {
        conversationId: this.conversation.id,
        teamId: this._team.id,
        paging,
      },
      signal,
    )
  }

  /**
   * Creates a content view model based on the message content type.
   *
   * @param message
   */
  private _createMessageContentViewModel(
    message: Message,
  ): MessageContentViewModel {
    switch (message.content.type) {
      case 'Progression':
        return new JobProgressionMessageContentViewModel(
          this._options.canUpdateJobProgressionAsClient,
          message,
          this._options.viewRejectDraftInReviewDialog,
          this._options.viewApproveDraftInReviewDialog,
          this._jobProgression,
        )
      default:
        return new TextMessageContentViewModel(message)
    }
  }
}

/**
 * What a job supplies to its conversation view model.
 */
export interface ConversationViewModelOptions {
  canManageConversation: boolean
  canUpdateJobProgressionAsClient: boolean
  viewApproveDraftInReviewDialog: () => void
  viewRejectDraftInReviewDialog: () => void
}

/**
 * Message group.
 */
export interface MessageGroup {
  sender: Member
  messages: MessageViewModel[]
  timestamp: Date
  showDateLine: boolean
  tag: MessageGroupTag
}

/**
 * Tag to add to the message group.
 */
export type MessageGroupTag = 'None' | 'Admin' | 'System' | 'Assistant'

/**
 * Compute the group tag for a message.
 *
 * @param message
 */
function groupTagForMessage(message: MessageViewModel): MessageGroupTag {
  switch (message.messageType) {
    case 'AdminMessage':
      return 'Admin'

    case 'SystemMessage':
      return 'System'

    case 'ExternalReference':
      switch (message.contentType) {
        case 'Assistant':
          return 'Assistant'

        default:
          return 'None'
      }

    default:
      return 'None'
  }
}
